import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import api from '../../api/client';
import StatusBadge from '../common/StatusBadge';
import useWebSocket from '../../api/websocket';

const LEVEL_OPTIONS = [
  { value: 'all', label: 'All Levels' },
  { value: 'info', label: 'Info' },
  { value: 'warning', label: 'Warning' },
  { value: 'error', label: 'Error' },
  { value: 'critical', label: 'Critical' },
];

const SOURCE_OPTIONS = [
  { value: 'all', label: 'All Sources' },
  { value: 'system', label: 'System' },
  { value: 'scheduler', label: 'Scheduler' },
  { value: 'automation', label: 'Automation' },
  { value: 'streaming', label: 'Streaming' },
  { value: 'ads', label: 'Advertising' },
  { value: 'media', label: 'Media' },
];

const LEVEL_COLORS = {
  info: 'text-blue-400 bg-blue-900/30',
  warning: 'text-warning-400 bg-warning-900/30',
  error: 'text-error-400 bg-error-900/30',
  critical: 'text-error-400 bg-error-900/50',
};

export default function AlertList() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [levelFilter, setLevelFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [resolvedOnly, setResolvedOnly] = useState(false);
  const [selectedAlerts, setSelectedAlerts] = useState([]);

  const { subscribe } = useWebSocket((data) => {
    if (data.type === 'alerts:update') {
      if (data.action === 'created') {
        setAlerts((prev) => [data.payload, ...prev]);
      } else if (data.action === 'resolved') {
        setAlerts((prev) =>
          prev.map((a) => (a.id === data.payload.id ? { ...a, is_resolved: true } : a))
        );
      }
    }
  });

  useEffect(() => {
    fetchAlerts();
    const unsub = subscribe('alerts:update', (data) => {
      if (data.action === 'created') {
        setAlerts((prev) => [data.payload, ...prev]);
      } else if (data.action === 'resolved') {
        setAlerts((prev) =>
          prev.map((a) => (a.id === data.payload.id ? { ...a, is_resolved: true } : a))
        );
      }
    });
    return unsub;
  }, [subscribe, levelFilter, sourceFilter, resolvedOnly]);

  const fetchAlerts = async () => {
    setLoading(true);
    try {
      const params = {};
      if (levelFilter !== 'all') params.level = levelFilter;
      if (sourceFilter !== 'all') params.source = sourceFilter;
      if (resolvedOnly) params.is_resolved = true;

      const { data } = await api.get('/api/alerts', { params });
      setAlerts(data);
    } catch (err) {
      console.error('[AlertList] Failed to fetch alerts:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async (alert) => {
    try {
      await api.put(`/api/alerts/${alert.id}/resolve`, {});
      setAlerts(alerts.map((a) => (a.id === alert.id ? { ...a, is_resolved: true } : a)));
      setSelectedAlerts(selectedAlerts.filter((id) => id !== alert.id));
    } catch (err) {
      console.error('[AlertList] Resolve failed:', err);
    }
  };

  const handleRestoreFromAlert = async (alert) => {
    const mediaId = alert.details && alert.details.media_file_id;
    if (!mediaId) return;
    try {
      await api.post(`/api/media/${mediaId}/restore`, {});
      setAlerts(alerts.map((a) => (a.id === alert.id ? { ...a, is_resolved: true } : a)));
    } catch (err) {
      alert('No se pudo restaurar el archivo. Puede que ya no esté marcado como corrupto.');
    }
  };

  const handleResolveSelected = async () => {
    try {
      await Promise.all(selectedAlerts.map((id) => api.put(`/api/alerts/${id}/resolve`, {})));
      setAlerts(alerts.map((a) =>
        selectedAlerts.includes(a.id) ? { ...a, is_resolved: true } : a
      ));
      setSelectedAlerts([]);
    } catch (err) {
      console.error('[AlertList] Bulk resolve failed:', err);
    }
  };

  const handleSelectAll = () => {
    setSelectedAlerts(
      resolvedOnly ? [] : alerts.filter((a) => !a.is_resolved).map((a) => a.id)
    );
  };

  const getLevelBadge = (level) => {
    const classes = LEVEL_COLORS[level] || 'text-dark-400 bg-dark-700';
    const icon = level === 'critical' ? '🔴' : level === 'error' ? '❌' : level === 'warning' ? '⚠' : 'ℹ';
    return (
      <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${classes}`}>
        <span>{icon}</span>
        {level.charAt(0).toUpperCase() + level.slice(1)}
      </span>
    );
  };

  const filteredAlerts = alerts.filter((a) => {
    if (levelFilter !== 'all' && a.level !== levelFilter) return false;
    if (sourceFilter !== 'all' && a.source !== sourceFilter) return false;
    if (resolvedOnly && !a.is_resolved) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="flex min-h-[300px] items-center justify-center">
        <p className="text-dark-400">Loading alerts...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-dark-100">Alertas</h1>
        <div className="flex gap-2">
          {!resolvedOnly && selectedAlerts.length > 0 && (
            <button
              onClick={handleResolveSelected}
              className="btn btn-success btn-sm"
              title="Resolve selected"
            >
              Resolve {selectedAlerts.length} selected
            </button>
          )}
          <button
            onClick={fetchAlerts}
            className="btn btn-secondary btn-sm"
            title="Refresh"
          >
            ↻
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-xl bg-dark-850 border border-dark-700 px-4 py-2 text-xs text-dark-400">
        <span>En las alertas de reproducción:</span>
        <span className="text-success-400">↩ Restaurar</span>
        <span>= quitar marca de corrupto (para sobreescribir el archivo)</span>
        <span className="text-primary-400">👁 Ver archivo</span>
        <span>= abrir/borrar el archivo</span>
        <span className="text-dark-300">✓ Resolver</span>
        <span>= marcar como atendida sin tocar el archivo</span>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <select
          value={levelFilter}
          onChange={(e) => setLevelFilter(e.target.value)}
          className="input text-sm w-40"
        >
          {LEVEL_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <select
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value)}
          className="input text-sm w-40"
        >
          {SOURCE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="resolved_only"
            checked={resolvedOnly}
            onChange={(e) => setResolvedOnly(e.target.checked)}
            className="h-4 w-4 rounded border-dark-700 text-primary-600 focus:ring-primary-600"
          />
          <label htmlFor="resolved_only" className="text-sm text-dark-300">
            Resolved only
          </label>
        </div>
      </div>

      {filteredAlerts.length === 0 ? (
        <div className="py-12 text-center text-dark-400">
          No alerts matching your filters.
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center justify-between border-b border-dark-700 pb-2">
            <label className="flex items-center gap-2 text-sm text-dark-300">
              <input
                type="checkbox"
                checked={
                  filteredAlerts.length > 0 &&
                  selectedAlerts.length === filteredAlerts.filter((a) => !a.is_resolved).length
                }
                onChange={handleSelectAll}
                className="h-4 w-4 rounded border-dark-700 text-primary-600 focus:ring-primary-600"
              />
              Select all unresolved
            </label>
          </div>

          {filteredAlerts
            .slice()
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
            .map((alert) => (
              <div
                key={alert.id}
                className={`rounded-xl border p-4 transition-colors ${
                  alert.is_resolved
                    ? 'bg-dark-800/30 border-dark-700'
                    : 'bg-dark-850 border-dark-700 hover:bg-dark-800/50'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    {!alert.is_resolved && (
                      <input
                        type="checkbox"
                        checked={selectedAlerts.includes(alert.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedAlerts([...selectedAlerts, alert.id]);
                          } else {
                            setSelectedAlerts(selectedAlerts.filter((id) => id !== alert.id));
                          }
                        }}
                        className="h-4 w-4 mt-0.5 rounded border-dark-700 text-primary-600 focus:ring-primary-600"
                      />
                    )}
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        {getLevelBadge(alert.level)}
                        <span className="text-xs text-dark-500">
                          [{alert.source || 'system'}]
                        </span>
                      </div>
                      <p className="mt-1 text-sm text-dark-200">
                        {typeof alert.message === 'object' ? JSON.stringify(alert.message) : alert.message}
                      </p>
                      {alert.details && (
                        <pre className="mt-2 text-xs text-dark-500 rounded bg-dark-800 p-2 overflow-x-auto">
                          {typeof alert.details === 'object'
                            ? JSON.stringify(alert.details, null, 2)
                            : alert.details}
                        </pre>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-dark-500">
                      {alert.created_at ? format(parseISO(alert.created_at), 'PPp') : '—'}
                    </span>
                    {alert.details && alert.details.media_file_id && (
                      <Link
                        to={`/media/${alert.details.media_file_id}`}
                        className="rounded-md bg-dark-700 px-2.5 py-1 text-xs text-primary-300 hover:bg-dark-600 hover:text-primary-200"
                        title="Abrir el archivo (ver/borrar/sobreescribir)"
                      >
                        👁 Ver archivo
                      </Link>
                    )}
                    {!alert.is_resolved && alert.details && alert.details.media_file_id && (
                      <button
                        onClick={() => handleRestoreFromAlert(alert)}
                        className="rounded-md bg-success-900/40 px-2.5 py-1 text-xs font-medium text-success-400 hover:bg-success-900/60 hover:text-success-300"
                        title="Quitar marca de corrupto (vuelve a la rotación para sobreescribirlo)"
                      >
                        ↩ Restaurar
                      </button>
                    )}
                    {!alert.is_resolved ? (
                      <button
                        onClick={() => handleResolve(alert)}
                        className="rounded-md bg-dark-700 px-2 py-1 text-xs text-dark-300 hover:bg-dark-600"
                        title="Marcar como resuelta sin tocar el archivo"
                      >
                        ✓ Resolver
                      </button>
                    ) : (
                      <StatusBadge status="completed" size="sm" />
                    )}
                  </div>
                </div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}
